import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from "@hangar/web-components/core/Button";
import { Text } from "@hangar/web-components/core/Text";
import { useTranslation } from '../../hooks/i18n/useTranslation';
import { HangarSelectField } from './HangarSelectField';
import { NOT_APPLICABLE_NON_NOTIFIABLE_RC_ID } from '../ChangeCodeForm/ChangeCodeForm.types';
import { useHangarFloatingPortalZIndexBoost } from './useHangarFloatingPortalZIndexBoost';
import * as S from './styles/NotApplicableModal.style';

export interface NotApplicableReason {
  readonly id: string;
  readonly labelKey: string;
}

type NotApplicableModalPropsBase = {
  isOpen: boolean;
  reasons: readonly NotApplicableReason[];
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
};

export type NotApplicableModalProps = NotApplicableModalPropsBase &
  (
    | { hideReasonSelect?: false; preSelectedReason?: string }
    | { hideReasonSelect: true; preSelectedReason: string }
  );

export function NotApplicableModal(props: NotApplicableModalProps) {
  const { isOpen, reasons, onConfirm, onCancel, isSubmitting = false } = props;
  const hideReasonSelect = props.hideReasonSelect === true;
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [hasError, setHasError] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  useHangarFloatingPortalZIndexBoost(isOpen);

  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setHasError(false);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (isSubmitting) return;
      onCancel();
      return;
    }

    if (e.key === 'Tab' && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [role="combobox"], [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, [onCancel, isSubmitting]);

  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      
      document.addEventListener('keydown', handleKeyDown);
      
      const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [role="combobox"], [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();

      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        
        previousActiveElement.current?.focus();
      };
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (isSubmitting) return;
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const handleReasonChange = (value: string) => {
    setSelectedReason(value);
    if (value) {
      setHasError(false);
    }
  };

  const handleConfirm = () => {
    if (isSubmitting) return;
    if (props.hideReasonSelect === true) {
      const reasonId =
        props.preSelectedReason.trim() || NOT_APPLICABLE_NON_NOTIFIABLE_RC_ID;
      onConfirm(reasonId);
      return;
    }
    if (!selectedReason) {
      setHasError(true);
      return;
    }
    onConfirm(selectedReason);
  };

  return createPortal(
    <S.Overlay onClick={handleOverlayClick} role="presentation">
      <S.ModalContainer
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="not-applicable-modal-title"
        aria-describedby="not-applicable-modal-description"
      >
        <S.ModalHeader>
          <S.HeaderContent>
            <Text variant="heading-md" bold={true} color="tertiary" id="not-applicable-modal-title">
              {t('flightChangeDetails.notApplicableConfirmation.title')}
            </Text>
          </S.HeaderContent>
          <S.CloseButton
            type="button"
            disabled={isSubmitting}
            onClick={onCancel}
            aria-label={t('common.close')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </S.CloseButton>
        </S.ModalHeader>
        
        <S.ModalBody>
          <Text variant="body-md" color="secondary" id="not-applicable-modal-description">
            {t('flightChangeDetails.notApplicableConfirmation.message')}
          </Text>
          
          {!hideReasonSelect && (
            <S.FormGroup>
              <S.SelectFieldBlock>
                <HangarSelectField
                  id="not-applicable-reason-select"
                  label={t('flightChangeDetails.notApplicableConfirmation.selectLabel')}
                  placeholder={t('flightChangeDetails.notApplicableConfirmation.selectPlaceholder')}
                  value={selectedReason}
                  options={reasons.map((reason) => ({
                    value: reason.id,
                    label: t(reason.labelKey),
                  }))}
                  onChange={handleReasonChange}
                  disabled={isSubmitting}
                  error={hasError}
                  errorText={
                    hasError
                      ? t('flightChangeDetails.notApplicableConfirmation.reasonRequired')
                      : ''
                  }
                />
              </S.SelectFieldBlock>
            </S.FormGroup>
          )}
        </S.ModalBody>
        
        <S.ModalFooter>
          <Button
            variant="secondary"
            label={t('flightChangeDetails.notApplicableConfirmation.cancel')}
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <Button
            variant="primary"
            loading={isSubmitting}
            aria-busy={isSubmitting}
            aria-disabled={isSubmitting}
            label={
              isSubmitting
                ? t('actionConfirmation.processing')
                : t('flightChangeDetails.notApplicableConfirmation.confirm')
            }
            onClick={handleConfirm}
          />
        </S.ModalFooter>
      </S.ModalContainer>
    </S.Overlay>,
    document.body,
  );
}
